/**
 * Web account deletion (the URL Google Play requires for "delete without the app").
 *
 * No dedicated backend endpoint is needed. Ownership is proved with the same
 * emailed-code flow the app uses, which yields a Firebase ID token from a fresh
 * sign-in, and that token is what `DELETE /api/auth/account` requires:
 *
 *   POST /api/auth/email/start   -> code emailed
 *   POST /api/auth/email/verify  -> custom token
 *   Identity Toolkit signInWithCustomToken -> ID token (fresh auth_time)
 *   DELETE /api/auth/account     -> account deleted
 *
 * Protocol details: backend/docs/auth/api.md.
 */

const API_BASE_URL = import.meta.env.VITE_NEW_WORDS_API_URL || 'http://localhost:8080'
const IDENTITY_TOOLKIT_URL = 'https://identitytoolkit.googleapis.com/v1/accounts:signInWithCustomToken'

export type DeletionErrorCode =
  | 'INVALID_INPUT'
  | 'INVALID_CODE'
  | 'CODE_LOCKED'
  | 'RATE_LIMITED'
  | 'REAUTH_REQUIRED'
  | 'DISABLED'
  | 'EMAIL_UNAVAILABLE'
  | 'NETWORK'
  | 'UNKNOWN'

export class DeletionError extends Error {
  constructor(public readonly code: DeletionErrorCode) {
    super(code)
    this.name = 'DeletionError'
  }
}

// Backend error codes (see backend/src/services/authErrors.ts) -> ours.
const BACKEND_CODES: Record<string, DeletionErrorCode> = {
  AUTH_INVALID_REQUEST: 'INVALID_INPUT',
  AUTH_OTP_INVALID: 'INVALID_CODE',
  AUTH_OTP_LOCKED: 'CODE_LOCKED',
  AUTH_RATE_LIMITED: 'RATE_LIMITED',
  AUTH_REAUTH_REQUIRED: 'REAUTH_REQUIRED',
  AUTH_METHOD_DISABLED: 'DISABLED',
  AUTH_EMAIL_UNAVAILABLE: 'EMAIL_UNAVAILABLE',
}

interface RequestOptions {
  method: 'POST' | 'DELETE'
  body?: unknown
  bearerToken?: string
}

async function request<T>(url: string, { method, body, bearerToken }: RequestOptions): Promise<T> {
  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new DeletionError('NETWORK')
  }

  const payload: unknown = await response.json().catch(() => ({}))
  if (!response.ok) {
    const code =
      typeof payload === 'object' && payload !== null && 'code' in payload
        ? String((payload as { code: unknown }).code)
        : ''
    throw new DeletionError(BACKEND_CODES[code] ?? 'UNKNOWN')
  }
  return payload as T
}

export const normalizeEmail = (email: string): string => email.trim().toLowerCase()

/** Emails a 6-digit code. The response is identical whether or not an account exists. */
export async function requestCode(email: string): Promise<{ resendAfterSec: number }> {
  const result = await request<{ resendAfterSec?: number }>(`${API_BASE_URL}/api/auth/email/start`, {
    method: 'POST',
    body: { email: normalizeEmail(email) },
  })
  return { resendAfterSec: result.resendAfterSec ?? 60 }
}

/** Verifies the code and returns a Firebase ID token from a fresh sign-in. */
export async function verifyCodeForIdToken(email: string, code: string): Promise<string> {
  const { customToken } = await request<{ customToken: string }>(`${API_BASE_URL}/api/auth/email/verify`, {
    method: 'POST',
    body: { email: normalizeEmail(email), code: code.trim() },
  })

  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY
  if (!apiKey) throw new DeletionError('UNKNOWN')

  const { idToken } = await request<{ idToken: string }>(
    `${IDENTITY_TOOLKIT_URL}?key=${encodeURIComponent(apiKey)}`,
    { method: 'POST', body: { token: customToken, returnSecureToken: true } }
  )
  return idToken
}

/** Permanently deletes the account the ID token belongs to. */
export async function deleteAccount(idToken: string): Promise<void> {
  await request<{ deleted: boolean }>(`${API_BASE_URL}/api/auth/account`, {
    method: 'DELETE',
    bearerToken: idToken,
  })
}

const MESSAGES: Record<DeletionErrorCode, string> = {
  INVALID_INPUT: 'Check your email address and the 6-digit code, then try again.',
  INVALID_CODE: 'That code is not right or has expired. Check it and try again, or request a new one.',
  CODE_LOCKED: 'Too many wrong attempts. Request a new code.',
  RATE_LIMITED: 'Too many requests. Wait a little and try again.',
  REAUTH_REQUIRED: 'For your security, please start again and use a fresh code.',
  DISABLED: 'Email sign-in is temporarily unavailable. Email privacy@zwizle.com to delete your account.',
  EMAIL_UNAVAILABLE: 'We could not send the email just now. Please try again in a few minutes.',
  NETWORK: 'Could not reach Zwizle. Check your connection and try again.',
  UNKNOWN: 'Something went wrong. Please try again, or email privacy@zwizle.com.',
}

export const getDeletionErrorMessage = (error: unknown): string =>
  MESSAGES[error instanceof DeletionError ? error.code : 'UNKNOWN']
